import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LOCAL_STORAGE_KEYS } from '../constants/local-storage-keys';
import { StorageService } from './storage.service';

/**
 * Interface for branding color configuration.
 */
export interface BrandingColors {
  /** Primary color used for buttons, links, and accents */
  primaryColor: string;
  /** Secondary color for complementary elements */
  secondaryColor: string;
}

/**
 * Interface for branding data received from the API.
 */
export interface BrandingResponse {
  /** Base64-encoded logo image */
  logoImage: string;
  /** JSON string containing BrandingColors object */
  branding: string | BrandingColors;
}

/**
 * Service for managing tenant branding (colors and logo).
 * Applies CSS custom properties dynamically based on selected tenant.
 *
 * @remarks
 * This service handles:
 * - Dynamic color theming via CSS variables
 * - Logo management (handled by header component)
 * - Branding persistence in storage
 * - Reactive branding updates via Observable
 *
 * The branding colors are applied as CSS custom properties:
 * - `--primary-color`: Applied to document root and body
 * - `--secondary-color`: Applied to document root and body
 *
 * @example
 * ```typescript
 * // Subscribe to branding changes
 * brandingService.branding$.subscribe(branding => {
 *   if (branding) {
 *     console.log('Branding updated');
 *   }
 * });
 *
 * // Set branding (typically called after tenant selection)
 * brandingService.setBranding(brandingResponse);
 *
 * // Clear branding (e.g., on logout)
 * brandingService.clearBranding();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class BrandingService {
  private storageService = inject(StorageService);
  private router = inject(Router);
  private readonly previewClassName = 'branding-preview-active';

  private brandingSubject = new BehaviorSubject<BrandingResponse | null>(null);
  private previewActive = false;
  private previewRestoreBranding: BrandingResponse | null = null;

  /**
   * Observable that emits whenever branding changes.
   */
  public branding$: Observable<BrandingResponse | null> = this.brandingSubject.asObservable();

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.previewActive) {
          this.resetPreviewBranding();
        }
      });
  }

  /**
   * Sets the branding configuration and applies it to the document.
   * Stores branding in localStorage and updates CSS custom properties.
   *
   * @param branding - The branding configuration from the API
   */
  setBranding(branding: BrandingResponse): void {
    this.previewActive = false;
    this.previewRestoreBranding = null;
    this.setPreviewClass(false);
    this.brandingSubject.next(branding);
    this.storageService.setItem(LOCAL_STORAGE_KEYS.BRANDING, branding);
    this.applyBranding(branding);
  }

  /**
   * Applies branding visually without persisting to storage.
   * Used for preview/temporary branding that should not survive navigation.
   * Updates the reactive branding state so header/logo previews also refresh,
   * but does not persist the preview to storage.
   */
  previewBranding(branding: BrandingResponse): void {
    if (!this.previewActive) {
      this.previewRestoreBranding =
        this.storageService.getItem<BrandingResponse>(LOCAL_STORAGE_KEYS.BRANDING) ??
        this.brandingSubject.value;
    }

    this.previewActive = true;
    this.setPreviewClass(true);
    this.brandingSubject.next(branding);
    this.applyBranding(branding);
  }

  /**
   * Gets the current branding configuration synchronously.
   *
   * @returns The current branding object, or null if no branding is set
   */
  getBranding(): BrandingResponse | null {
    return this.brandingSubject.value;
  }

  /**
   * Internal method to load branding from storage.
   * Not used on init to prevent flashing with wrong branding.
   */
  private loadBrandingFromStorage(): void {
    const branding = this.storageService.getItem<BrandingResponse>(LOCAL_STORAGE_KEYS.BRANDING);
    if (branding) {
      this.brandingSubject.next(branding);
      this.applyBranding(branding);
    }
  }

  /**
   * Internal method to apply branding colors as CSS custom properties.
   * Applies to both document root and body for maximum specificity.
   *
   * @param branding - The branding configuration to apply
   */
  private applyBranding(branding: BrandingResponse): void {
    try {
      const colors = this.parseBrandingColors(branding.branding);

      // Apply CSS variables to document root
      const root = document.documentElement;
      root.style.setProperty('--primary-color', colors.primaryColor);
      root.style.setProperty('--secondary-color', colors.secondaryColor);
      root.style.setProperty('--brand-primary', colors.primaryColor);
      root.style.setProperty('--brand-secondary', colors.secondaryColor);
      root.style.setProperty('--color-ui-primary-bg', colors.primaryColor);
      root.style.setProperty('--color-ui-secondary-bg', colors.secondaryColor);
      root.style.setProperty('--color-ui-secondary-border', this.toAlphaColor(colors.secondaryColor, 0.3));

      // Also apply directly to body to ensure higher specificity
      const body = document.body;
      body.style.setProperty('--primary-color', colors.primaryColor);
      body.style.setProperty('--secondary-color', colors.secondaryColor);
      body.style.setProperty('--brand-primary', colors.primaryColor);
      body.style.setProperty('--brand-secondary', colors.secondaryColor);
      body.style.setProperty('--color-ui-primary-bg', colors.primaryColor);
      body.style.setProperty('--color-ui-secondary-bg', colors.secondaryColor);
      body.style.setProperty('--color-ui-secondary-border', this.toAlphaColor(colors.secondaryColor, 0.3));

      // Force reflow to ensure styles are applied
      void root.offsetHeight;

      // Apply logo if available
      if (branding.logoImage) {
        // Logo is handled by header component
      }
    } catch {
      // Error applying branding - use default colors
    }
  }

  private parseBrandingColors(branding: BrandingResponse['branding']): BrandingColors {
    const defaults: BrandingColors = {
      primaryColor: '#0B1F3A',
      secondaryColor: '#1FA3FF',
    };

    if (!branding) {
      return defaults;
    }

    if (typeof branding === 'string') {
      const parsed = JSON.parse(branding) as Partial<BrandingColors>;
      return {
        primaryColor: parsed.primaryColor || defaults.primaryColor,
        secondaryColor: parsed.secondaryColor || defaults.secondaryColor,
      };
    }

    return {
      primaryColor: branding.primaryColor || defaults.primaryColor,
      secondaryColor: branding.secondaryColor || defaults.secondaryColor,
    };
  }

  private toAlphaColor(color: string, alpha: number): string {
    const normalized = color.trim();

    if (/^#([0-9a-f]{6})$/i.test(normalized)) {
      const hex = normalized.slice(1);
      const red = parseInt(hex.slice(0, 2), 16);
      const green = parseInt(hex.slice(2, 4), 16);
      const blue = parseInt(hex.slice(4, 6), 16);
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }

    if (/^#([0-9a-f]{3})$/i.test(normalized)) {
      const hex = normalized.slice(1);
      const red = parseInt(hex[0] + hex[0], 16);
      const green = parseInt(hex[1] + hex[1], 16);
      const blue = parseInt(hex[2] + hex[2], 16);
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }

    return color;
  }

  /**
   * Clears the branding configuration and resets CSS variables to defaults.
   * Removes branding from storage and clears the BehaviorSubject.
   */
  clearBranding(): void {
    this.previewActive = false;
    this.previewRestoreBranding = null;
    this.setPreviewClass(false);
    this.brandingSubject.next(null);
    this.storageService.removeItem(LOCAL_STORAGE_KEYS.BRANDING);

    // Reset CSS variables to defaults
    const root = document.documentElement;
    const body = document.body;
    root.style.setProperty('--primary-color', '#0B1F3A');
    root.style.setProperty('--secondary-color', '#1FA3FF');
    root.style.setProperty('--brand-primary', '#0B1F3A');
    root.style.setProperty('--brand-secondary', '#1FA3FF');
    root.style.setProperty('--color-ui-primary-bg', '#0B1F3A');
    root.style.setProperty('--color-ui-secondary-bg', '#1FA3FF');
    root.style.setProperty('--color-ui-secondary-border', 'rgba(31, 163, 255, 0.3)');
    body.style.setProperty('--primary-color', '#0B1F3A');
    body.style.setProperty('--secondary-color', '#1FA3FF');
    body.style.setProperty('--brand-primary', '#0B1F3A');
    body.style.setProperty('--brand-secondary', '#1FA3FF');
    body.style.setProperty('--color-ui-primary-bg', '#0B1F3A');
    body.style.setProperty('--color-ui-secondary-bg', '#1FA3FF');
    body.style.setProperty('--color-ui-secondary-border', 'rgba(31, 163, 255, 0.3)');
  }

  private resetPreviewBranding(): void {
    const brandingToRestore = this.previewRestoreBranding;
    this.previewActive = false;
    this.previewRestoreBranding = null;
    this.setPreviewClass(false);

    if (brandingToRestore) {
      this.brandingSubject.next(brandingToRestore);
      this.applyBranding(brandingToRestore);
      return;
    }

    this.brandingSubject.next(null);
    this.resetCssVariablesToDefaults();
  }

  private resetCssVariablesToDefaults(): void {
    const root = document.documentElement;
    const body = document.body;
    root.style.setProperty('--primary-color', '#0B1F3A');
    root.style.setProperty('--secondary-color', '#1FA3FF');
    root.style.setProperty('--brand-primary', '#0B1F3A');
    root.style.setProperty('--brand-secondary', '#1FA3FF');
    root.style.setProperty('--color-ui-primary-bg', '#0B1F3A');
    root.style.setProperty('--color-ui-secondary-bg', '#1FA3FF');
    root.style.setProperty('--color-ui-secondary-border', 'rgba(31, 163, 255, 0.3)');
    body.style.setProperty('--primary-color', '#0B1F3A');
    body.style.setProperty('--secondary-color', '#1FA3FF');
    body.style.setProperty('--brand-primary', '#0B1F3A');
    body.style.setProperty('--brand-secondary', '#1FA3FF');
    body.style.setProperty('--color-ui-primary-bg', '#0B1F3A');
    body.style.setProperty('--color-ui-secondary-bg', '#1FA3FF');
    body.style.setProperty('--color-ui-secondary-border', 'rgba(31, 163, 255, 0.3)');
  }

  private setPreviewClass(isActive: boolean): void {
    document.body.classList.toggle(this.previewClassName, isActive);
  }
}
